import { useMemo } from "react"
import { AxisValue, DashBoardConditions } from "features/dashBoard/types"
import { CommonGraphStyle, selectedItemStyle, filter } from "../../common"
import { usePieGraphOption } from "./options"
import { round } from "shared/util"
import { DashBoardValue } from "features/dashBoard/hooks"
import { DownLoadData } from "components/download/csv/types"

type usePieGraphModelProps<T> = {
  dashBoard: DashBoardValue<T>
	dataList: T[] | null
	conditions: DashBoardConditions,
	filterText: string | undefined
	graphOption: ReturnType<typeof usePieGraphOption<T>>
}
export const usePieGraphModel = <T,>({
	dashBoard: {
		subSource: {
			lv: { dataState: lvLogListState },
			mrp: { dataState: mrpLogListState }
		}
	},
	dataList,
	conditions,
	filterText,
	graphOption: {
		configure, targetList,
    axisDef, valueDef,
    sort, total
  }
}: usePieGraphModelProps<T>) => useMemo(
	() => {
		if (!dataList || dataList.length === 0) return null
		if (!axisDef?.key || !valueDef?.key) return null

		const condition = conditions[axisDef.key]

		type PieData = {
			code: string
			name: string
			value: number
			itemStyle?: CommonGraphStyle
		}
		const data: PieData[] = []
		const totalData: PieData[] = []
		const addData = (data: PieData[], axis: AxisValue | AxisValue[] | undefined, value: number) => {
			if (!axis) return
			const axisList = Array.isArray(axis) ? axis : [axis]
			for (const ax of axisList) {
				if (filterText && ax.name) {
					const axName = ax.name
					if (filterText.split(' ').every((t) => axName.indexOf(t) < 0)) continue
				}
				let it = data.find((r) => r.code === ax.code)
				if (!it) {
					it = {
						code: ax.code,
						name: ax.name || '',
						value: value,
						itemStyle: selectedItemStyle(condition, ax)
					}
					data.push(it)
				} else {
					it.value = it.value + value
				}
			}
		}
		if (total) {
			if (axisDef.source === 'mrp') {
				const valByMrpLog = axisDef.valByMrpLog
				if (valByMrpLog) {
					const logs = mrpLogListState.data?.list || []
					for(const log of logs) {
						addData(totalData, valByMrpLog(log), 1)
					}
				}
			} else if (axisDef.source === 'lv') {
				const valByLvLog = axisDef.valByLvLog
				if (valByLvLog) {
					const logs = lvLogListState.data?.list || []
					for(const log of logs) {
						addData(totalData, valByLvLog(log), 1)
					}
				}
			}
		}
		for (const record of dataList) {
			const axis = axisDef.val(record)
			if (!axis) continue
			const value = valueDef.val(record)
			if (value === undefined || value === 0) continue
			if (!filter(record, configure, conditions, axisDef.key, targetList)) continue
			addData(data, axis, value)
		}
		data.sort((a, b) => sort ? (b.value - a.value) : a.code.localeCompare(b.code))
		totalData.sort((a, b) => sort ? (b.value - a.value) : a.code.localeCompare(b.code))

		return {
			data,
			totalData,
			csvBuilder: (): DownLoadData | undefined => {
				if (!axisDef?.key || !valueDef?.key) return undefined
	
				const axisKey = axisDef.key
				const valueKey = valueDef.key
				const valueTotal = data.reduce((total, data) => total += data.value, 0)
				if (valueTotal <= 0) return undefined
				return {
					header: [
						{ title: axisDef.title, key: axisDef.key },
						{ title: valueDef.title, key: valueDef.key, unit: valueDef.unit },
						{ title: '割合', key: 'percentage', unit: '%' }
					],
					body: data.map((data) => ({
						[axisKey]: data.name,
						[valueKey]: data.value,
						percentage: round((data.value * 100) / valueTotal, 1)
					}))
				}
			}
		}
	},
	[
		configure, targetList, conditions,
		dataList, lvLogListState, mrpLogListState,
		axisDef, valueDef, sort, total, filterText
	]
)
